import { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/toastContext';
import { Box, Server, Cpu, HardDrive, Activity, RefreshCw, Loader2 } from 'lucide-react';

interface Sandbox {
  id: string;
  name: string;
  status: 'idle' | 'allocated' | 'recycling';
  sandbox_type: string;
  resource_limit: { cpu: number; memory_mb: number; disk_mb: number };
  endpoint_url: string | null;
  tenant_id: string | null;
  tenant_name?: string;
  current_runtime_id: string | null;
  created_at: string;
}

interface TenantUsage {
  tenant_id: string;
  tenant_name: string;
  quotas: {
    max_sandboxes: number | null;
    max_cpu: number | null;
    max_memory_mb: number | null;
    pool_size: number | null;
  };
  usage: {
    sandbox_count: number;
    total_cpu: number;
    total_memory_mb: number;
  };
  utilization: {
    sandbox_pct: number | null;
    cpu_pct: number | null;
    memory_pct: number | null;
  };
}

interface Stats {
  total: number;
  allocated: number;
  idle: number;
  recycling: number;
  tenant_breakdown: { tenant_id: string | null; tenant_name: string | null; sandbox_count: number }[];
}

export default function SandboxAdmin() {
  const toast = useToast();
  const [sandboxes, setSandboxes] = useState<Sandbox[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [tenantUsages, setTenantUsages] = useState<TenantUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterTenant, setFilterTenant] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [refilling, setRefilling] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [sbRes, statsRes] = await Promise.all([
        apiClient.get('/admin/sandboxes'),
        apiClient.get('/admin/sandboxes/stats'),
      ]);
      setSandboxes(sbRes.data.sandboxes || []);
      setStats(statsRes.data);

      // Load per-tenant usage
      const usages: TenantUsage[] = [];
      for (const tb of statsRes.data.tenant_breakdown || []) {
        if (tb.tenant_id) {
          try {
            const uRes = await apiClient.get(`/admin/sandboxes/tenants/${tb.tenant_id}/usage`);
            usages.push(uRes.data);
          } catch {
            // ignore
          }
        }
      }
      setTenantUsages(usages);
    } catch (e) {
      console.error('Failed to load sandboxes:', e);
      toast.error('加载沙箱数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      void loadData();
    };
    tick();
  }, []);

  const refillPool = async (tenantId: string) => {
    setRefilling(tenantId);
    try {
      await apiClient.post(`/admin/sandboxes/tenants/${tenantId}/pool/refill`);
      await loadData();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
      const detail = err.response?.data?.detail;
      const msg = (typeof detail === 'string' && detail) || (typeof err.message === 'string' && err.message) || '未知错误';
      toast.error('Pool refill failed: ' + msg);
    } finally {
      setRefilling(null);
    }
  };

  const filteredSandboxes = sandboxes.filter(sb => {
    if (filterTenant && sb.tenant_id !== filterTenant) return false;
    if (filterStatus && sb.status !== filterStatus) return false;
    return true;
  });

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      idle: 'success',
      allocated: 'warning',
      recycling: 'error',
    };
    return <span className={`badge ${map[status] || 'secondary'}`}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4">
        <h1><Box className="inline mr-2" size={24} /> Sandbox Admin</h1>
        <button className="secondary" onClick={loadData}><RefreshCw size={16} className="inline mr-1" /> 刷新</button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="card">
            <div className="flex items-center gap-2 mb-2">
              <Server size={20} className="text-primary" />
              <span className="text-sm text-muted">Total Sandboxes</span>
            </div>
            <div className="text-2xl font-bold">{stats.total}</div>
          </div>
          <div className="card">
            <div className="flex items-center gap-2 mb-2">
              <Activity size={20} className="text-success" />
              <span className="text-sm text-muted">Idle</span>
            </div>
            <div className="text-2xl font-bold">{stats.idle}</div>
          </div>
          <div className="card">
            <div className="flex items-center gap-2 mb-2">
              <Cpu size={20} className="text-warning" />
              <span className="text-sm text-muted">Allocated</span>
            </div>
            <div className="text-2xl font-bold">{stats.allocated}</div>
          </div>
          <div className="card">
            <div className="flex items-center gap-2 mb-2">
              <HardDrive size={20} className="text-error" />
              <span className="text-sm text-muted">Recycling</span>
            </div>
            <div className="text-2xl font-bold">{stats.recycling}</div>
          </div>
        </div>
      )}

      {/* Tenant Usage */}
      {tenantUsages.length > 0 && (
        <div className="card mb-6">
          <h3 className="mb-3">Tenant Resource Usage</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Sandboxes</th>
                  <th>CPU</th>
                  <th>Memory</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenantUsages.map(tu => (
                  <tr key={tu.tenant_id}>
                    <td>{tu.tenant_name}</td>
                    <td>
                      {tu.usage.sandbox_count} / {tu.quotas.max_sandboxes ?? '∞'}
                      {tu.utilization.sandbox_pct !== null && (
                        <span className={`badge ${tu.utilization.sandbox_pct > 90 ? 'error' : tu.utilization.sandbox_pct > 70 ? 'warning' : 'success'} ml-2`}>
                          {tu.utilization.sandbox_pct}%
                        </span>
                      )}
                    </td>
                    <td>
                      {tu.usage.total_cpu.toFixed(1)} / {tu.quotas.max_cpu ?? '∞'}
                      {tu.utilization.cpu_pct !== null && (
                        <span className={`badge ${tu.utilization.cpu_pct > 90 ? 'error' : tu.utilization.cpu_pct > 70 ? 'warning' : 'success'} ml-2`}>
                          {tu.utilization.cpu_pct}%
                        </span>
                      )}
                    </td>
                    <td>
                      {tu.usage.total_memory_mb} / {tu.quotas.max_memory_mb ?? '∞'} MB
                      {tu.utilization.memory_pct !== null && (
                        <span className={`badge ${tu.utilization.memory_pct > 90 ? 'error' : tu.utilization.memory_pct > 70 ? 'warning' : 'success'} ml-2`}>
                          {tu.utilization.memory_pct}%
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        className="secondary text-xs"
                        onClick={() => refillPool(tu.tenant_id)}
                        disabled={refilling === tu.tenant_id}
                      >
                        {refilling === tu.tenant_id ? <Loader2 size={12} className="inline animate-spin" /> : <RefreshCw size={12} className="inline mr-1" />}
                        Refill Pool
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <select value={filterTenant} onChange={e => setFilterTenant(e.target.value)} className="text-sm">
          <option value="">All Tenants</option>
          {stats?.tenant_breakdown.map(tb => (
            <option key={tb.tenant_id ?? 'null'} value={tb.tenant_id ?? ''}>
              {tb.tenant_name || 'Default'} ({tb.sandbox_count})
            </option>
          ))}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="text-sm">
          <option value="">All Status</option>
          <option value="idle">Idle</option>
          <option value="allocated">Allocated</option>
          <option value="recycling">Recycling</option>
        </select>
      </div>

      {/* Sandbox List */}
      {filteredSandboxes.length === 0 ? (
        <div className="empty">
          <h3>No sandboxes</h3>
          <p>Try adjusting filters or initialize a tenant pool.</p>
        </div>
      ) : (
        <div className="list">
          {filteredSandboxes.map(sb => (
            <div key={sb.id} className="list-item">
              <div className="list-item-content">
                <div className="flex items-center gap-2">
                  <h3 style={{ margin: 0 }}>{sb.name}</h3>
                  {statusBadge(sb.status)}
                  <span className="badge secondary text-xs">{sb.sandbox_type}</span>
                </div>
                <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
                  <span>CPU: {sb.resource_limit?.cpu ?? 1} cores</span>
                  <span className="ml-2">Memory: {sb.resource_limit?.memory_mb ?? 512} MB</span>
                  <span className="ml-2">Disk: {sb.resource_limit?.disk_mb ?? 1024} MB</span>
                </p>
                {sb.endpoint_url && (
                  <p className="text-muted text-sm" style={{ margin: '2px 0 0' }}>
                    Endpoint: {sb.endpoint_url}
                  </p>
                )}
                {sb.current_runtime_id && (
                  <p className="text-sm" style={{ margin: '2px 0 0' }}>
                    <span className="text-warning">Runtime: {sb.current_runtime_id}</span>
                  </p>
                )}
                <p className="text-muted text-xs" style={{ margin: '2px 0 0' }}>
                  Created: {new Date(sb.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
